/**
 * OpportunityCard Component
 * 
 * Displays an opportunity in a list context.
 * Matches v2 design system with consistent spacing and typography.
 */

import React, { useRef } from 'react';
import { View, Text, Pressable, StyleSheet, Animated } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, text, space, radius, icon, touch } from '../../../shared/tokens/mobile';
import { OpportunityStatus } from '../../../shared/types/opportunity';
import { formatCurrency } from '../../../shared/utils/formatters';

interface OpportunityCardProps {
  name: string;
  location?: string;
  acreage?: number | null;
  askingPrice?: number | null;
  status: OpportunityStatus;
  onPress: () => void;
  hideBorder?: boolean;
}

const STATUS_CONFIG: Record<OpportunityStatus, { label: string; color: string }> = {
  evaluating: { label: 'Evaluating', color: color.info },
  under_contract: { label: 'Contract', color: color.warning },
  owned: { label: 'Owned', color: color.success },
  selling: { label: 'Selling', color: color.warning },
  sold: { label: 'Sold', color: color.textMuted },
  passed: { label: 'Passed', color: color.disabled },
};

export function OpportunityCard({
  name,
  location,
  acreage,
  askingPrice,
  status,
  onPress,
  hideBorder = false,
}: OpportunityCardProps) {
  const scale = useRef(new Animated.Value(1)).current;
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.evaluating;

  const handlePressIn = () => {
    Animated.spring(scale, { toValue: 0.98, useNativeDriver: true, speed: 50 }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scale, { toValue: 1, useNativeDriver: true, speed: 50 }).start();
  };

  // Format metrics
  const metricsArray: string[] = [];
  if (acreage) metricsArray.push(`${acreage.toFixed(1)} ac`);
  if (askingPrice) metricsArray.push(formatCurrency(askingPrice));
  const metricsDisplay = metricsArray.join(' • ');

  return (
    <Pressable
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      accessibilityRole="button"
      accessibilityLabel={`${name}, ${statusConfig.label}`}
    >
      <Animated.View style={[styles.container, !hideBorder && styles.border, { transform: [{ scale }] }]}>
        {/* Icon */}
        <View style={styles.iconContainer}>
          <Ionicons name="location-outline" size={icon.md} color={color.textSecondary} />
        </View>

        {/* Content */}
        <View style={styles.content}>
          <Text style={styles.name} numberOfLines={1}>{name}</Text>
          {location && <Text style={styles.location} numberOfLines={1}>{location}</Text>}
          {metricsDisplay && <Text style={styles.metrics}>{metricsDisplay}</Text>}
        </View>

        {/* Status */}
        <View style={styles.statusContainer}>
          <View style={[styles.statusDot, { backgroundColor: statusConfig.color }]} />
          <Text style={[styles.statusText, { color: statusConfig.color }]}>{statusConfig.label}</Text>
        </View>

        {/* Chevron */}
        <Ionicons name="chevron-forward" size={icon.sm + 2} color={color.textMuted} />
      </Animated.View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: touch.listRow,
    paddingVertical: space.md,
    paddingHorizontal: space.md,
    gap: space.sm,
    backgroundColor: color.surface,
  },
  border: {
    borderBottomWidth: 1,
    borderBottomColor: color.divider,
  },
  iconContainer: {
    width: icon.lg + space.xs,
    alignItems: 'center',
  },
  content: {
    flex: 1,
    gap: 2,
  },
  name: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    lineHeight: text.body.lineHeight,
    color: color.textPrimary,
  },
  location: {
    fontSize: text.caption.fontSize,
    fontWeight: text.caption.fontWeight,
    lineHeight: text.caption.lineHeight,
    color: color.textSecondary,
  },
  metrics: {
    fontSize: text.caption.fontSize,
    fontWeight: text.caption.fontWeight,
    lineHeight: text.caption.lineHeight,
    color: color.textMuted,
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.xs,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  statusText: {
    fontSize: text.caption.fontSize,
    fontWeight: '600',
  },
});
